import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Icon from '../../misc/Icon';
import { StyledFieldLabel } from './SettingsCommonElements';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const StyledWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StyledInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledInputAddon = styled.div`
  padding-right: 8px;
  padding-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => `border: ${props.theme.borderWidth}px solid ${props.theme.borderColor};`}
  svg {
    color: ${(props) => props.theme.fontColorPrimary};
  }
`;

const inputStyles = (theme) => {
  return `
    flex: 1;
    background-color: ${theme.backgroundColor};
    font-size: ${theme.fontSize};
    color: ${theme.fontColorPrimary};
    border: ${theme.borderWidth}px solid ${theme.borderColor};
    padding: 8px;
    transition: all 0.15s ease-in-out;
  `;
};

const inputActiveStyles = (theme) => {
  return `
    &:hover, &:focus {
      outline: unset; 
      border-style: solid;
      border-width: 1px !important;
      border-right-color: ${theme.primaryColor} !important;
      border-left-color: ${theme.primaryColor} !important;
      border-color: ${theme.primaryColor};
    }
  `;
};

const StyledInput = styled.input`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${(props) => inputStyles(props.theme)}
  ${(props) => {
    if (props.disabled) return '';
    return inputActiveStyles(props.theme);
  }}

`;

const StyledTextarea = styled.textarea`
  ${(props) => inputStyles(props.theme)}
  ${(props) => {
    if (props.disabled) return '';
    return inputActiveStyles(props.theme);
  }}

`;

const StyledSelect = styled.select`
  width: 100%;
  ${(props) => inputStyles(props.theme)}
  ${(props) => {
    if (props.disabled) return '';
    return inputActiveStyles(props.theme);
  }}
  &:hover {
    cursor: pointer;
  }
`;

const StyledDescription = styled.div`
  width: 100%;
  font-size: 12px;
  margin: 2px 0px 0px 3px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const StyledIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;

  svg {
    font-size: 22px;
  }
`;

class InputSettingsField extends React.PureComponent {

  static contextType = ThemeContext

  static propTypes = {
    id: PropTypes.string,
    fieldId: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.any,
    addon: PropTypes.string,
    description: PropTypes.string,
    rows: PropTypes.number,
    disabled: PropTypes.bool,
    inputStyles: PropTypes.object,
    onChange: PropTypes.func,
    onChangeText: PropTypes.func,
    onClear: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
  }

  static defaultProps = {
    value: '',
    rows: 3,
    inputStyles: {}
  }

  constructor(props) {
    super(props);
    const { value } = props;
    this.state = { focused: false, changes: value };
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { value } = this.props;
    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });
  }

  componentWillUnmount() {

    const { value, onChange, fieldId } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && onChange) {
      onChange(fieldId, changes);
    }

  }

  render() {

    const { changes, focused } = this.state;

    const {
      id,
      fieldId,
      type,
      label,
      value,
      addonBefore,
      addonAfter,
      description,
      rows,
      disabled,
      onChange,
      onChangeText,
      onClear,
      onFocus,
      onBlur,
      inputStyles,
      error,
      options
    } = this.props;

    const handleChange = (changes) => {

      const nextValue = type === 'number' ? parseFloat(changes, 10) : changes;

      if (nextValue !== value && onChange) onChange(fieldId, nextValue);

    };

    const fieldProps = {
      disabled,
      error,
      value: changes,
      onChange: (e) => {
        this.setState({ changes: e.target.value });
        if (type === 'dropdown') handleChange(e.target.value);
        if (type !== 'dropdown' && onChangeText) onChangeText(e.target.value);
      },
      onFocus: () => {
        if (!focused) this.setState({ focused: true });
        if (onFocus) onFocus();
      },
      onBlur: (e) => {
        if (focused) this.setState({ focused: false });
        if (onBlur) onBlur();
        if (type !== 'dropdown') handleChange(e.target.value);
      }
    };

    /**
     * >>>>>>>>>> Start Field Input <<<<<<<<<<<<
     */
    const { borderRadius, borderColor, primaryColor } = this.context;
    const nextInputStyles = { ...inputStyles };

    let inputBorderRadius = `${borderRadius}px`;
    if (addonBefore && addonAfter) inputBorderRadius = '0px';
    else if (addonAfter) inputBorderRadius = `${borderRadius}px 0px 0px ${borderRadius}px`;
    else if (addonBefore) inputBorderRadius = `0px ${borderRadius}px ${borderRadius}px 0px`;

    let inputBorderRightColor = borderColor;
    let inputBorderLeftColor = borderColor;

    if (addonBefore) inputBorderLeftColor = 'transparent';
    if (addonAfter) inputBorderRightColor = 'transparent';

    nextInputStyles.borderRadius = inputBorderRadius;
    nextInputStyles.borderRightColor = inputBorderRightColor;
    nextInputStyles.borderLeftColor = inputBorderLeftColor;

    let inputNode = <StyledInput tabIndex={-1} theme={this.context} data-testid={`${id}-input-settings-input`} style={nextInputStyles} type={type} {...fieldProps} />;
    if (type === 'textarea') inputNode = <StyledTextarea tabIndex={-1} theme={this.context} data-testid={`${id}-input-settings-textarea`} style={nextInputStyles} rows={rows} {...fieldProps} />;
    if (type === 'dropdown') {

      const optionNodes = [];
      options.forEach((option) => {
        if (option.deleted) return;
        optionNodes.push(
          <option
            key={option._id}
            value={option._id}
          >
            {option.value}
          </option>
        );
      });

      inputNode = (
        <StyledSelect
          tabIndex={-1}
          theme={this.context}
          data-testid={`${id}-input-settings-select`}
          style={nextInputStyles}
          {...fieldProps}
        >
          <option value="" />
          {optionNodes}
        </StyledSelect>
      );
    }

    /**
     * >>>>>>>>>> End Field Input <<<<<<<<<<<<
     */

    /**
     * >>>>>>>>>> Start Addon Before <<<<<<<<<<<<
     */
    const addonBeforeNode = !addonBefore ? null : (
      <StyledInputAddon
        theme={this.context}
        style={{
          borderRadius: `${borderRadius}px 0px 0px ${borderRadius}px`,
          borderRightWidth: '0px',
          marginRight: '-1px'
        }}
      >
        {addonBefore}
      </StyledInputAddon>
    );
    /**
     * >>>>>>>>>> End Addon Before <<<<<<<<<<<<
     */

    /**
     * >>>>>>>>>> Start Addon After <<<<<<<<<<<<
     */
    const addonAfterNode = !addonAfter ? null : (
      <StyledInputAddon
        theme={this.context}
        style={{
          borderRadius: `0px ${borderRadius}px ${borderRadius}px 0px`,
          borderLeftWidth: '0px',
          marginLeft: '-1px'
        }}
      >
        {addonAfter}
      </StyledInputAddon>
    );
    /**
     * >>>>>>>>>> End Addon After <<<<<<<<<<<<
     */

    return (
      <StyledWrapper>
        {!label ? null : (
          <StyledFieldLabel theme={this.context}>
            {label}
          </StyledFieldLabel>
        )}
        <StyledInputWrapper>
          {addonBeforeNode}
          {inputNode}
          {addonAfterNode}
          {!value || !onClear ? null : (
            <StyledIcon theme={this.context}>
              <Icon
                id={`${id}-input-settings-clear`}
                icon={ThemeIcons.settings.clear}
                color={borderColor}
                hoverColor={primaryColor}
                onClick={onClear}
              />
            </StyledIcon>
          )}
        </StyledInputWrapper>
        {!description ? null : <StyledDescription theme={this.context}>{description}</StyledDescription>}

      </StyledWrapper>
    );

  }

}

export default InputSettingsField;
